import { Component, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { HtlService } from "../htl/htl.service";
import { ActivatedRoute } from "@angular/router";
import { SheetDescriptor, SpreadsheetComponent } from "@progress/kendo-angular-spreadsheet";
import { MatDialog } from "@angular/material/dialog";
import { EditQtbDialogComponent } from "./edit-qtb-dialog/edit-qtb-dialog.component";
import { EventEmitter } from "@angular/core";
import { EditQtbCancelDialogComponent } from "./edit-qtb-cancel-dialog/edit-qtb-cancel-dialog.component";
import { environment } from "../../environments/environment";

@Component({
    selector: "app-edit-qtb",
    templateUrl: "./edit-qtb.component.html",
    styleUrls: ["./edit-qtb.component.scss"],
    standalone: false,
})
export class EditQtbComponent implements OnInit, OnChanges {
    @Input()
    public documentID: any;

    @Input()
    public blockID: any;

    @Input()
    public helicopterID: any;

    @Output()
    public getOriginalPdfPath = new EventEmitter<string>();

    @Output()
    public getCurrentPdfPath = new EventEmitter<string>();

    @Output()
    public setEditQtb = new EventEmitter<boolean>();

    @Output()
    public getNotes = new EventEmitter<string>();

    public canEdit = false;

    public sheets: SheetDescriptor[] = [];

    private ignoreRepeat = false;

    private editReason: string;

    public hasWeightErrors = false;
    public hasDateErrors = false;

    isLoading = true;

    @ViewChild("excelChild") spreadsheet: SpreadsheetComponent;

    public spreadsheetOld;

    public saveSpreadsheetJSON: {
        columnName: string;
        columnValue: string;
    }[] = [];

    public JSONTemplate;

    @Output() onSaveEvent: EventEmitter<any> = new EventEmitter();

    constructor(
        private _htlService: HtlService,
        private _route: ActivatedRoute,
        private _dialog: MatDialog,
    ) {}

    ngOnInit(): void {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["helicopterID"] || changes["blockID"] || changes["documentID"]) {
            this.fetchDocument();
        }
    }

    // Fetches document based on parameters from backend

    fetchDocument() {
        this.isLoading = true;
        this._htlService.generateHtlDocument(this.helicopterID, this.blockID, this.documentID).subscribe((res) => {
            this.getOriginalPdfPath.emit(res.downloadOriginalLink);
            this.getCurrentPdfPath.emit(res.downloadLink);
            if (res?.notes?.length > 0) {
                this.getNotes.emit(res.notes);
            }

            if (environment.local) {
                // Convert base64 to Blob
                const byteCharacters = atob(res.xlsxData);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: res.xlsxContentType });

                // Load blob into spreadsheet
                this.canEdit = false;
                this.spreadsheet.spreadsheetWidget
                    .fromFile(blob)
                    .then(() => {
                        setTimeout(() => {
                            this.formatTable();
                            this.spreadsheetOld = this.spreadsheet.spreadsheetWidget.toJSON();
                            this.isLoading = false;
                        }, 1000);
                    })
                    .catch((err) => console.log(err));
            } else if (res.downloadXlsxLink) {
                fetch(res.downloadXlsxLink)
                    .then((resp) => resp.blob())
                    .then((blob) => {
                        this.canEdit = false;
                        this.spreadsheet.spreadsheetWidget.fromFile(blob);
                    })
                    .then(() => {
                        setTimeout(() => {
                            this.formatTable();
                            this.spreadsheetOld = this.spreadsheet.spreadsheetWidget.toJSON();
                            this.isLoading = false;
                        }, 1000);
                    })
                    .catch((err) => console.log(err));
            }
        });
    }

    public onSave() {
        if (this.hasWeightErrors || this.hasDateErrors) {
        } else {
            const dialogRef = this._dialog.open(EditQtbDialogComponent, {
                disableClose: true,
                width: "300px",
                data: {},
            });
            dialogRef.afterClosed().subscribe((res: any) => {
                if (res.data.confirmed) {
                    this.onSaveEvent.emit();
                    this.editReason = res.data.reason;
                    this.canEdit = false;
                    this.generateJSONTemplate();
                    this._htlService.saveEditedColumns(this.JSONTemplate).subscribe({
                        next: () => window.location.reload(),
                    });
                }
            });
        }
    }

    public onSpreadsheetChange() {
        if (!this.spreadsheetOld) {
            return false;
        }

        // Don't do anything if spreadsheet is changed by code

        if (this.ignoreRepeat) {
            this.ignoreRepeat = false;
            return false;
        }

        const cellData = this.spreadsheet.spreadsheetWidget.sheets()[0].activeCell();
        const rowIndex = cellData.topLeft.row + 1;
        const colIndex = cellData.topLeft.col;

        let currentCol = "";

        if (colIndex >= 26) {
            currentCol += "A" + String.fromCharCode(65 + colIndex - 26);
        } else {
            currentCol = String.fromCharCode(65 + colIndex);
        }

        const currentCell = currentCol + rowIndex;
        const currentRange = currentCol + rowIndex + ":" + currentCol + rowIndex;

        const editRange = this.spreadsheet.spreadsheetWidget.sheets()[0].range(currentRange);

        const oldCell = this.spreadsheetOld.sheets[0].rows.filter((r: any) => r.index === rowIndex - 1)[0].cells[colIndex];

        // If user inserts empty value or deletes value prevent it and set back to old value

        if (editRange.value() === null || editRange.value() === "") {
            // If value was changed previously revert it back to changed value, else revert it back to original value

            const changedRange = this.saveSpreadsheetJSON.filter((s) => s.columnName === currentCell)[0];

            if (changedRange && oldCell.value) {
                this.ignoreRepeat = true;
                editRange.value(changedRange.columnValue);
            } else {
                this.ignoreRepeat = true;
                if (oldCell.value) {
                    editRange.value(oldCell.value);
                } else {
                    editRange.value("");
                }
                if (oldCell.background) {
                    editRange.background(oldCell.background);
                } else {
                    editRange.background("rgb(255, 255, 255)");
                }
            }
        } else {
            // Format HH:mm values

            const colonIndex = editRange.value().indexOf(":");

            if (colonIndex >= 0 && editRange.value().substring(colonIndex + 1, editRange.value().length).length > 2) {
                this.ignoreRepeat = true;
                editRange.value(editRange.value().substring(0, colonIndex) + ":" + editRange.value().substring(colonIndex + 1, colonIndex + 3));
            }

            // Format lt/kg values for specific cells AA7:AB22

            if (
                currentRange === "AA7:AA7" ||
                currentRange === "AA11:AA11" ||
                currentRange === "AA15:AA15" ||
                currentRange === "AA19:AA19" ||
                currentRange === "AB7:AB7" ||
                currentRange === "AB9:AB9" ||
                currentRange === "AB11:AB11" ||
                currentRange === "AB13:AB13" ||
                currentRange === "AB15:AB15" ||
                currentRange === "AB17:AB17" ||
                currentRange === "AB19:AB19" ||
                currentRange === "AB21:AB21"
            ) {
                const editText = editRange.value();
                if (
                    !editText
                        .substring(editText.length - 3, editText.length)
                        .toLowerCase()
                        .includes(" lt") &&
                    !editText
                        .substring(editText.length - 3, editText.length)
                        .toLowerCase()
                        .includes(" kg")
                ) {
                    editRange.background("rgb(255, 0, 0)");
                    this.hasWeightErrors = true;
                    return false;
                } else {
                    this.hasWeightErrors = false;
                }
            }

            // Format date value for specific cell Z53:AA53

            if (currentRange === "Z53:Z53") {
                const editText = editRange.value();
                if (!editText.match(/^.*\d{2}-\d{2}-\d{4}$/)) {
                    editRange.background("rgb(255, 0, 0)");
                    this.hasDateErrors = true;
                    return false;
                } else {
                    this.hasDateErrors = false;
                }
            }

            // If user reinserts old value change color back to original color and delete from JSON

            if (oldCell.value && oldCell.value.trim() === editRange.value().trim()) {
                if (oldCell.background) {
                    editRange.background(oldCell.background);
                } else {
                    editRange.background("rgb(255, 255, 255)");
                }

                this.saveSpreadsheetJSON.splice(
                    this.saveSpreadsheetJSON.indexOf(
                        this.saveSpreadsheetJSON.filter((s) => {
                            return s.columnName === currentCell;
                        })[0],
                    ),
                    1,
                );
            } else {
                editRange.background("rgb(255, 255, 0)");

                const cellExists = this.saveSpreadsheetJSON.filter((s) => s.columnName === currentCell);

                // If value already exists in JSON edit value

                if (cellExists.length > 0) {
                    cellExists[0].columnValue = editRange.value();

                    // If value does not exist add new entry to JSON
                } else {
                    this.saveSpreadsheetJSON.push({
                        columnName: currentCell,
                        columnValue: editRange.value(),
                    });
                }
            }
        }
        return true;
    }

    // Generate the JSON file to send to backend

    generateJSONTemplate() {
        this.saveSpreadsheetJSON.forEach((cell: any) => {
            cell.columnValue = cell.columnValue.trim();
            let colonIndex = cell.columnValue.indexOf(":");
            if (colonIndex >= 0) {
                if (cell.columnValue.substring(0, colonIndex).length < 2) {
                    cell.columnValue = "0" + cell.columnValue;
                    colonIndex += 1;
                }

                if (cell.columnValue.substring(colonIndex + 1, cell.columnValue.length).length === 1) {
                    cell.columnValue = cell.columnValue.substring(0, colonIndex) + ":0" + cell.columnValue.substring(colonIndex + 1, cell.columnValue.length);
                }
            }
            const commaIndex = cell.columnValue.indexOf(",");
            if (commaIndex >= 0) {
                cell.columnValue = cell.columnValue.substring(0, commaIndex) + "." + cell.columnValue.substring(commaIndex + 1, cell.columnValue.length);
            }
        });
        this.JSONTemplate = {
            htlBlockId: this._route.snapshot.queryParams["block"],
            htlDocumentId: this._route.snapshot.queryParams["document"],
            helicopterId: this._route.snapshot.queryParams["helicopter"],
            spreadsheets: [
                {
                    spreadsheetIndex: 0,
                    spreadsheetName: this.spreadsheet.activeSheet,
                    notes: this.editReason,
                    columns: JSON.parse(JSON.stringify(this.saveSpreadsheetJSON)),
                },
            ],
        };
    }

    // On excel sheet load disable specific cells so they are not editable

    enableEditing() {
        this._htlService.getEnabledCells().subscribe((res) => {
            if (res.length > 0) {
                res[0].columns.forEach((cell) => {
                    this.spreadsheet.spreadsheetWidget.sheets()[0].range(cell.columnName).enable(true);
                });
                this.canEdit = true;
            }
        });
    }

    // On excel sheet load format table to be all text

    formatTable() {
        this.spreadsheet.spreadsheetWidget.sheets()[0].range("A1:AX500").format("@");

        // Font sizes formatting so texts are visible
        this.spreadsheet.spreadsheetWidget.sheets()[0].range("A1:AA1").fontSize(11);
        this.spreadsheet.spreadsheetWidget.sheets()[0].range("B3:B6").fontSize(11);
        this.spreadsheet.spreadsheetWidget.sheets()[0].range("F5:F6").fontSize(11);
        this.spreadsheet.spreadsheetWidget.sheets()[0].range("E4:E6").fontSize(11);
        this.spreadsheet.spreadsheetWidget.sheets()[0].range("X5:X6").fontSize(11);
        this.spreadsheet.spreadsheetWidget.sheets()[0].range("AB5:AE6").fontSize(9);
        this.spreadsheet.spreadsheetWidget.sheets()[0].range("Q7:R22").fontSize(10);
        this.spreadsheet.spreadsheetWidget.sheets()[0].range("AD24:AH24").fontSize(10);
        this.spreadsheet.spreadsheetWidget.sheets()[0].range("Y35:Y35").fontSize(10);
        this.spreadsheet.spreadsheetWidget.sheets()[0].range("O41:O41").fontSize(10);
        this.spreadsheet.spreadsheetWidget.sheets()[0].range("P48:S48").fontSize(10);
        this.spreadsheet.spreadsheetWidget.sheets()[0].range("W48:W48").fontSize(10);
        this.spreadsheet.spreadsheetWidget.sheets()[0].range("X46:Z46").fontSize(10);
        this.spreadsheet.spreadsheetWidget.sheets()[0].range("AG3:AH6").fontSize(12);

        this.spreadsheet.spreadsheetWidget.sheets()[0].range("A1:AX500").enable(false);
        if (this.spreadsheet.spreadsheetWidget.sheets().length > 1) {
            this.spreadsheet.spreadsheetWidget.sheets()[1].range("A1:AX500").format("@");
            this.spreadsheet.spreadsheetWidget.sheets()[1].range("A1:AX500").enable(false);
        }
        this._htlService.getEditedCells(this.documentID).subscribe((res) => {
            if (res.length > 0) {
                res[0].columns.forEach((cell: any) => {
                    if (cell.autoCalculated === 1) {
                        this.spreadsheet.spreadsheetWidget
                            .sheets()[0]
                            .range(cell.columnName + ":" + cell.columnName)
                            .color("rgb(0, 200, 0");
                    } else {
                        this.spreadsheet.spreadsheetWidget
                            .sheets()[0]
                            .range(cell.columnName + ":" + cell.columnName)
                            .color("rgb(255, 0, 0");
                    }
                });
            }
        });
    }

    onStopEdit() {
        if (this.saveSpreadsheetJSON.length > 0) {
            const dialogRef = this._dialog.open(EditQtbCancelDialogComponent, {
                disableClose: true,
                width: "300px",
                data: {},
            });
            dialogRef.afterClosed().subscribe((res: any) => {
                if (res) {
                    window.location.reload();
                }
            });
        } else {
            this.canEdit = false;
            this.spreadsheet.spreadsheetWidget.sheets()[0].range("A1:AX500").enable(false);
            this.setEditQtb.emit(false);
        }
    }
}
